import React, { useCallback, useImperativeHandle, useState } from "react";
import type { Ref } from "react";
import { isFunction } from "@common/helpers/typeGuards";
import { OAuthServiceType } from "@common/constants";
import { StripeLanding } from "@common/schemas/oauth.schema";
import { useWindowMessage } from "@hooks/useWindowMessage";
import { useInteraction } from "@hooks/useInteraction";
import { useEntities } from "@hooks/useEntities";
import { OAuthAuthorizationResponse, triggerAuthorization } from "@lib/oauth";
import { useStripeConnectionCreationMutation } from "@api/stripe.api";
import {
    ConnectionActionButton,
    ConnectionButtonSize,
    ConnectionButtonVariant,
} from "@components/financialConnection/ConnectionActionButton";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import {
    AddAccountProps,
    CreateAccountHandle,
} from "../accounts/AddAccounts/lib";

interface Props extends AddAccountProps {
    children?:
        | React.ReactNode
        | ((props: {
              connecting: boolean;
              initializeConnection: () => void;
          }) => React.ReactNode);
    btnVariant?: ConnectionButtonVariant;
    btnSize?: ConnectionButtonSize;
    toastMessage?: string;
    /** Which Stripe page the OAuth popup opens on. Defaults to Stripe's own choice (sign-up). */
    stripeLanding?: StripeLanding;
}
export const CreateStripeConnection = ({
    children = <>Connect</>,
    onCreated,
    defaultEntity: defaultEntityProp,
    btnVariant = "secondary",
    toastMessage,
    btnSize = "sm",
    stripeLanding,
    ref,
}: Props & { ref?: Ref<CreateAccountHandle> }) => {
    const mutation = useStripeConnectionCreationMutation({
        onSuccess: onCreated,
        toastMessage,
    });
    const businessEntities = useEntities({
        onlyBusiness: true,
        excludeMockEntities: true,
    });
    const defaultEntity =
        defaultEntityProp ??
        (businessEntities.length === 1 ? businessEntities[0] : undefined);
    const [creating, setCreating] = useState(false);
    const handler = ({ code }: OAuthAuthorizationResponse) => {
        if (creating) {
            mutation.mutate({
                authCode: code,
                defaultEntityId: defaultEntity?.id,
            });
        }
        setCreating(false);
    };

    useWindowMessage<OAuthAuthorizationResponse>({
        scope: OAuthServiceType.STRIPE,
        handler,
    });

    const { refuseIfViewOnly } = useViewOnlyRefusal();

    const initializeConnection = useCallback(() => {
        if (creating) {
            return;
        }

        // The next step leaves the app, so the server never gets to refuse
        // it. Refuse here instead of after the provider's consent screen.
        if (refuseIfViewOnly()) {
            return;
        }

        triggerAuthorization(OAuthServiceType.STRIPE, { stripeLanding });
        setCreating(true);
    }, [creating, refuseIfViewOnly, stripeLanding]);

    const authorize = useInteraction(initializeConnection);

    useImperativeHandle(
        ref,
        () => ({
            initializeConnection,
        }),
        [initializeConnection],
    );

    if (isFunction(children)) {
        return children({ connecting: creating, initializeConnection });
    }

    return (
        <span {...authorize}>
            <ConnectionActionButton
                loading={mutation.isPending}
                variant={btnVariant}
                size={btnSize}
            >
                {children}
            </ConnectionActionButton>
        </span>
    );
};
